/**
 * Google Analytics 4 (GA4) Tracking Events
 *
 * This file defines all tracking events sent to Google Analytics throughout the application.
 * Events help track user behavior, conversion funnels, and feature usage to inform product decisions.
 *
 * IMPORTANT: Changes to event names will be reflected in Google Analytics, so be careful when changing them.
 * Renaming events will break historical data continuity and dashboards.
 *
 * Event Categories:
 * - Recommended GA4 Events: Standard events that follow Google's naming conventions
 * - Custom Events: Application-specific events for tracking unique user behaviors
 *
 * Usage:
 * ```javascript
 * import { sendTrackingEvent, TRACKING_EVENTS } from './analytics/trackingEvent';
 *
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.SEARCH,
 *   parameters: {
 *     search_term: 'yoga',
 *     results_count: 15
 *   }
 * });
 * ```
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
import { convertMoneyToNumber } from '../util/currency';
import { ensureListing, ensureUser } from '../util/data';
import { getPriceFromListing } from '../util/price';
import { showCreateListingLinkForUser } from '../util/userHelpers';
import { isGroupSession } from '../util/soulwise-marketplace/listingType';
import { getSubscriptionTrackingFields } from '../util/digitalSubscription';

/**
 * Tracking Events Constants
 *
 * This object contains all event names used throughout the application.
 * Use these constants instead of hardcoded strings to prevent typos and enable refactoring.
 */
export const TRACKING_EVENTS = {
  // ========================================
  // RECOMMENDED GA4 EVENTS
  // Standard events that follow Google Analytics 4 conventions
  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
  // ========================================

  /**
   * SEARCH Event (GA4 Recommended)
   *
   * @trigger Fires automatically after search results are loaded on the search page or landing page.
   *          This includes searches by keyword, category, location, dates, price range, or any combination of filters.
   * @businessValue Understand what users are searching for, popular filters, and search effectiveness.
   *                Helps identify gaps in listings and optimize search functionality.
   * @ga4Category recommended
   */
  SEARCH: 'search',

  /**
   * SELECT_ITEM Event (GA4 Recommended)
   *
   * @trigger Fires when user clicks anywhere on a listing card from search results, landing page, or similar events section.
   *          Does not fire if listing is clicked from other contexts like user profiles.
   * @businessValue Track which listings attract the most interest and measure conversion from browse to detail view.
   *                Helps identify popular providers and listing types.
   * @ga4Category recommended
   */
  SELECT_ITEM: 'select_item',

  /**
   * VIEW_ITEM Event (GA4 Recommended)
   *
   * @trigger Fires when user views a listing detail page, after the listing information loads.
   *          This happens when someone clicks on a listing to see its full details and description.
   * @businessValue Measure listing engagement and identify which listings attract the most interest.
   *                Track the conversion funnel from listing view to booking initiation.
   * @ga4Category recommended
   */
  VIEW_ITEM: 'view_item',

  /**
   * BEGIN_CHECKOUT Event (GA4 Recommended)
   *
   * @trigger Fires on checkout page load when user has selected dates/times and is ready to enter payment information.
   *          This marks the start of the payment flow.
   * @businessValue Critical conversion funnel metric. Compare with PURCHASE to measure checkout completion rate.
   *                Helps identify checkout abandonment and potential friction points.
   * @ga4Category recommended
   */
  BEGIN_CHECKOUT: 'begin_checkout',

  /**
   * PURCHASE Event (GA4 Recommended)
   *
   * @trigger Fires when payment is successfully processed and booking is confirmed.
   *          This is the final conversion event in the booking funnel.
   * @businessValue Primary revenue and conversion metric. Essential for ROI calculations, revenue reporting,
   *                and understanding which listings and categories drive the most bookings.
   * @ga4Category recommended
   */
  PURCHASE: 'purchase',

  /**
   * SHARE Event (GA4 Recommended)
   *
   * @trigger Fires when user clicks the share button on listings or profiles.
   *          Currently implemented but usage may be limited.
   * @businessValue Measure viral potential and user advocacy. Understand which content users find valuable enough to share.
   * @ga4Category recommended
   */
  SHARE_BUTTON_CLICKED: 'share',

  /**
   * LOGIN Event (GA4 Recommended)
   *
   * @trigger Fires immediately after user successfully logs in using email/password, Facebook, or Google authentication.
   *          Does not fire for automatic session restoration.
   * @businessValue Measure login success rate and authentication method usage.
   *                Compare with LOGIN_ATTEMPT and LOGIN_FAILED to calculate authentication success rate.
   * @ga4Category recommended
   */
  LOGIN: 'login',

  /**
   * LOGOUT Event (GA4 Recommended)
   *
   * @trigger Fires when user clicks the logout button and session is terminated.
   * @businessValue Understand session patterns and user engagement duration.
   * @ga4Category recommended
   */
  LOGOUT: 'logout',

  /**
   * SIGN_UP Event (GA4 Recommended)
   *
   * @trigger Fires immediately after user successfully completes registration,
   *          whether through email/password signup or OAuth providers (Google, Facebook).
   *          This event fires before the automatic login that follows signup.
   * @businessValue Measure signup conversion rates and track which authentication methods
   *                users prefer for registration. Essential for measuring user acquisition
   *                and optimizing signup funnel. Compare with LOGIN to understand returning vs new users.
   * @ga4Category recommended
   */
  SIGN_UP: 'sign_up',

  // ========================================
  // CUSTOM EVENTS
  // Application-specific events for tracking unique behaviors
  // ========================================

  /**
   * LOGIN_ATTEMPT Event (Custom)
   *
   * @trigger Fires immediately when user submits the login form, before authentication completes.
   *          This event fires regardless of whether login succeeds or fails.
   * @businessValue Combined with LOGIN and LOGIN_FAILED events, helps calculate authentication success/failure rates
   *                and identify authentication issues affecting user experience.
   * @ga4Category custom
   */
  LOGIN_ATTEMPT: 'login_attempt',

  /**
   * LOGIN_FAILED Event (Custom)
   *
   * @trigger Fires when login authentication fails (wrong password, non-existent account, or server error).
   *          Fires after the authentication request returns an error.
   * @businessValue Identify authentication problems, potential account issues, and areas where users need help.
   *                High failure rates may indicate UX issues or security concerns.
   * @ga4Category custom
   */
  LOGIN_FAILED: 'login_failed',

  /**
   * OPEN_ORDER_MODAL Event (Custom)
   *
   * @trigger Fires when user clicks the "Book" or similar button on a listing page on mobile/tablet devices,
   *          opening the order modal overlay. Does not fire on desktop where the panel is always visible.
   * @businessValue Measure mobile booking intent and engagement.
   *                Helps understand mobile user behavior differences from desktop users.
   * @ga4Category custom
   */
  OPEN_ORDER_MODAL: 'open_order_modal',

  /**
   * SHARE_DIALOG_OPENED Event (Custom)
   *
   * @trigger Fires when user clicks the share icon/button on listing or profile pages,
   *          opening the modal that shows sharing options (copy link, social media, etc.).
   * @businessValue Measure share intent (dialog opens) separately from actual shares.
   *                Helps understand if users are interested in sharing but may not complete the action.
   * @ga4Category custom
   */
  SHARE_DIALOG_OPENED: 'share_dialog_opened',

  /**
   * CLEAR_SEARCH Event (Custom)
   *
   * @trigger Fires when user clicks the "Clear" or "Reset filters" button on the landing page,
   *          removing all active search filters and returning to unfiltered results.
   * @businessValue Understand if users are getting stuck with too many filters or not finding desired results.
   *                May indicate need for better default search or filter UX improvements.
   * @ga4Category custom
   */
  CLEAR_SEARCH: 'clear_search',

  /**
   * PENDING_REVIEW_CTA_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks the "Leave a review" CTA in the pending review banner on Landing Page.
   * @businessValue Measure how often users act on review reminders from Landing Page.
   * @ga4Category custom
   */
  PENDING_REVIEW_CTA_CLICKED: 'pending_review_cta_clicked',

  /**
   * FILTERS_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks the "Filters" button on landing page, opening the dropdown/modal
   *          that shows additional filter options (price, language, mode, etc.).
   * @businessValue Measure filter engagement and understand how many users need advanced filtering options.
   *                Helps prioritize which filters should be more prominent.
   * @ga4Category custom
   */
  FILTERS_BUTTON_CLICKED: 'filters_button_clicked',

  /**
   * BOOKING_SUBMIT_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks "Book Now", "Request Booking", or similar primary action button
   *          on the listing page after selecting dates/times. This is before the checkout page loads.
   * @businessValue Critical for measuring booking intent and conversion funnel.
   *                Shows user commitment before entering payment flow.
   * @ga4Category custom
   */
  BOOKING_SUBMIT_BUTTON_CLICKED: 'booking_submit_button_clicked',

  /**
   * SEARCH_DATES_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks the "Select Dates" or date picker button on the search page,
   *          opening the calendar interface to choose date range for availability search.
   * @businessValue Measure how many users filter by dates.
   *                Helps understand importance of date-based search and calendar UX.
   * @ga4Category custom
   */
  SEARCH_DATES_BUTTON_CLICKED: 'search_dates_button_clicked',

  /**
   * SHOW_MORE_LISTINGS_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks "Show More" or "Load More" button at the bottom of search results
   *          on the landing page to load the next page of listings.
   * @businessValue Understand search result engagement depth and if users explore beyond first page.
   *                Helps optimize result page size and pagination UX.
   * @ga4Category custom
   */
  SHOW_MORE_LISTINGS_BUTTON_CLICKED: 'show_more_listings_button_clicked',

  /**
   * CATEGORY_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks any category button in the horizontal category bar on landing page
   *          (e.g., "Yoga", "Meditation", "Breathwork"). Fires both when selecting and deselecting a category.
   * @businessValue Measure category popularity and understand user interests.
   *                Helps prioritize content creation and provider recruitment for specific categories.
   * @ga4Category custom
   */
  CATEGORY_BUTTON_CLICKED: 'category_button_clicked',

  /**
   * SEARCH_WITH_NO_RESULTS Event (Custom)
   *
   * @trigger Fires automatically after a search completes and returns 0 listings.
   *          This is sent in addition to the regular SEARCH event.
   * @businessValue Critical for identifying gaps in listing inventory.
   *                Helps understand unfulfilled demand and where to recruit more providers.
   * @ga4Category custom
   */
  SEARCH_WITH_NO_RESULTS: 'search_with_no_results',

  /**
   * USER_TRY_ON_DISCOUNT_CODE_VALIDATION_FAILED Event (Custom)
   *
   * @trigger Fires when user enters a discount code on checkout page and validation fails
   *          (code expired, invalid, not applicable to listing, already used, etc.).
   * @businessValue Identify problematic discount codes and understand code usage patterns.
   *                May indicate need for better code validation UX or error messages.
   * @ga4Category custom
   */
  USER_TRY_ON_DISCOUNT_CODE_VALIDATION_FAILED: 'user_try_on_discount_code_validation_failed',

  /**
   * USER_TRY_ON_DISCOUNT_CODE_VALIDATION_SUCCEEDED Event (Custom)
   *
   * @trigger Fires when user enters a valid discount code on checkout page and it's successfully applied.
   *          Fires before the actual purchase is completed.
   * @businessValue Measure discount code effectiveness and promotion success rates.
   *                Helps calculate ROI of discount campaigns and understand price sensitivity.
   * @ga4Category custom
   */
  USER_TRY_ON_DISCOUNT_CODE_VALIDATION_SUCCEEDED: 'user_try_on_discount_code_validation_succeeded',

  /**
   * SIMILAR_EVENTS_BUTTON_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks the "Explore Similar Events" or similar CTA button
   *          on transaction/booking confirmation pages, redirecting to search with similar criteria.
   * @businessValue Measure cross-selling potential and user interest in browsing more offerings.
   *                Helps optimize recommendation engine and increase booking frequency.
   * @ga4Category custom
   */
  SIMILAR_EVENTS_BUTTON_CLICKED: 'similar_events_button_clicked',

  /**
   * CONTACT_SUPPORT_TO_CANCEL_BOOKING Event (Custom)
   *
   * @trigger Fires when user clicks "Contact Support" link in the booking cancellation information alert,
   *          typically shown when a booking cannot be cancelled through self-service.
   * @businessValue Measure cancellation support demand and identify bookings that need support intervention.
   *                Helps understand pain points in cancellation flow and optimize self-service options.
   * @ga4Category custom
   */
  CONTACT_SUPPORT_TO_CANCEL_BOOKING: 'contact_support_to_cancel_booking',

  /**
   * REMOVE_TIMESLOT_CONFIRMED Event (Custom)
   *
   * @trigger Fires when listing provider clicks "Remove Timeslot" confirmation button in the removal modal
   *          on the listing availability management page, actually deleting the timeslot.
   * @businessValue Measure provider behavior and availability management patterns.
   *                Helps understand scheduling challenges and provider needs.
   * @ga4Category custom
   */
  REMOVE_TIMESLOT_CONFIRMED: 'remove_timeslot_confirmed',

  /**
   * REMOVE_TIMESLOT_CANCELLED Event (Custom)
   *
   * @trigger Fires when listing provider clicks "Cancel" in the timeslot removal confirmation modal,
   *          abandoning the removal action and keeping the timeslot.
   * @businessValue Understand provider hesitation and decision-making process.
   *                May indicate need for better tools or warnings before timeslot removal.
   * @ga4Category custom
   */
  REMOVE_TIMESLOT_CANCELLED: 'remove_timeslot_cancelled',

  /**
   * CONTACT_SUPPORT_FROM_REMOVE_TIMESLOT_MODAL Event (Custom)
   *
   * @trigger Fires when listing provider clicks "Contact Support" link within the timeslot removal modal,
   *          opening support chat instead of completing the removal themselves.
   * @businessValue Identify cases where providers need help with availability management.
   *                May indicate UX issues or need for more provider education.
   * @ga4Category custom
   */
  CONTACT_SUPPORT_FROM_REMOVE_TIMESLOT_MODAL: 'contact_support_from_remove_timeslot_modal',

  /**
   * NO_FIRST_TIMESLOTS Event (Custom)
   *
   * @trigger Fires automatically when system checks for available timeslots on a listing and finds none.
   *          This is a system event, not a user action, fired during listing page load.
   * @businessValue Identify listings with availability problems that may hurt bookings.
   *                Helps prioritize provider outreach to update calendars and maintain listing quality.
   * @ga4Category custom
   */
  NO_FIRST_TIMESLOTS: 'no_first_timeslots',

  /**
   * PWA_INSTALL_CTA_CLICKED Event (Custom)
   *
   * @trigger Fires when user clicks the "Try installing" CTA in InstallAppBar.
   * @businessValue Measure interest in installing the PWA from the persistent banner.
   * @ga4Category custom
   */
  PWA_INSTALL_CTA_CLICKED: 'pwa_install_cta_clicked',

  /**
   * SAVED_LISTING Event (Custom)
   *
   * @trigger Fires after user successfully saves a listing (API call succeeds).
   * @businessValue Track Save feature usage, user distribution, and impact on engagement/purchases.
   * @ga4Category custom
   */
  SAVED_LISTING: 'saved_listing',

  /**
   * UNSAVED_LISTING Event (Custom)
   *
   * @trigger Fires after user successfully unsaves a listing.
   * @businessValue Track Save feature usage alongside SAVED_LISTING for complete funnel.
   * @ga4Category custom
   */
  UNSAVED_LISTING: 'unsaved_listing',

  /**
   * SAVE_ACTION_FAILED Event (Custom)
   *
   * @trigger Fires when save or unsave API call fails.
   * @businessValue Identify failed saves/unsaves for debugging and reliability monitoring.
   * @ga4Category custom
   */
  SAVE_ACTION_FAILED: 'save_action_failed',

  /**
   * SAVED_EVENTS_PAGE_VIEWED Event (Custom)
   *
   * @trigger Fires when Saved Events page loads and saved items have been fetched.
   * @businessValue Measure engagement with Saved Events page and saved_count per user.
   * @ga4Category custom
   */
  SAVED_EVENTS_PAGE_VIEWED: 'saved_events_page_viewed',

  /**
   * Practitioner onboarding checklist viewed
   * @ga4Category custom
   */
  PRACTITIONER_CHECKLIST_VIEWED: 'practitioner_checklist_viewed',

  /**
   * Practitioner onboarding checklist step completed
   * @ga4Category custom
   */
  PRACTITIONER_CHECKLIST_STEP_COMPLETED: 'practitioner_checklist_step_completed',

  /**
   * Practitioner onboarding checklist fully completed
   * @ga4Category custom
   */
  PRACTITIONER_CHECKLIST_COMPLETED: 'practitioner_checklist_completed',

  /**
   * Practitioner onboarding checklist CTA clicked
   * @ga4Category custom
   */
  PRACTITIONER_CHECKLIST_CTA_CLICKED: 'practitioner_checklist_cta_clicked',

  /**
   * Customer picked monthly vs yearly on subscription checkout.
   * @ga4Category custom
   */
  SUBSCRIPTION_PLAN_SELECTED: 'subscription_plan_selected',

  /**
   * Trial started at checkout (nothing charged today).
   * @ga4Category custom
   */
  TRIAL_STARTED: 'trial_started',

  /**
   * Paid subscribe with no trial.
   * @ga4Category custom
   */
  SUBSCRIPTION_STARTED: 'subscription_started',

  /**
   * Manage subscription → Stripe Customer Portal.
   * @ga4Category custom
   */
  SUBSCRIPTION_MANAGE_CLICKED: 'subscription_manage_clicked',

  /**
   * Expired/canceled listing CTA back to checkout.
   * @ga4Category custom
   */
  SUBSCRIPTION_RESUBSCRIBE_CLICKED: 'subscription_resubscribe_clicked',

  /**
   * Practitioner changed Pricing access mode.
   * @ga4Category custom
   */
  LISTING_ACCESS_MODE_SELECTED: 'listing_access_mode_selected',

  /**
   * Practitioner saved a subscription offering on Pricing.
   * @ga4Category custom
   */
  LISTING_SUBSCRIPTION_CONFIGURED: 'listing_subscription_configured',

  /**
   * AUTH_PAGE_VIEW Event (Custom)
   *
   * @trigger Fires once when the login or signup tab of AuthenticationPage mounts.
   * @params tab, is_free_content_flow, from_page, webview, language
   * @ga4Category custom
   */
  AUTH_PAGE_VIEW: 'auth_page_view',

  /**
   * AUTH_TAB_SWITCH Event (Custom)
   *
   * @trigger Fires when the user clicks the Log in / Sign up tab link.
   * @params from_tab, to_tab, is_free_content_flow
   * @ga4Category custom
   */
  AUTH_TAB_SWITCH: 'auth_tab_switch',

  /**
   * LOGIN_GOOGLE_CLICK Event (Custom)
   *
   * @trigger Fires when the user taps Continue with Google on the login tab,
   *          before navigating to the IdP. Distinct from the GA4 `login` event,
   *          which now means success.
   * @params is_webview, webview
   * @ga4Category custom
   */
  LOGIN_GOOGLE_CLICK: 'login_google_click',

  /**
   * SIGNUP_GOOGLE_CLICK Event (Custom)
   *
   * @trigger Fires when the user taps Sign up / Continue with Google on the
   *          signup tab, before navigating to the IdP.
   * @params is_webview, webview
   * @ga4Category custom
   */
  SIGNUP_GOOGLE_CLICK: 'signup_google_click',

  /**
   * AUTH_WEBVIEW_BLOCKED Event (Custom)
   *
   * @trigger Fires when Google/Facebook is tapped inside a detected in-app
   *          browser and the handoff dialog is shown instead of starting OAuth.
   * @params webview, provider, tab, from_page, handoff_target (auth | original)
   * @ga4Category custom
   */
  AUTH_WEBVIEW_BLOCKED: 'auth_webview_blocked',

  /**
   * AUTH_WEBVIEW_OPEN_IN_BROWSER_CLICK Event (Custom)
   *
   * @trigger Fires when the user taps Open in browser on the webview dialog
   *          (Android intent only).
   * @params webview, os, from_page, handoff_target
   * @ga4Category custom
   */
  AUTH_WEBVIEW_OPEN_IN_BROWSER_CLICK: 'auth_webview_open_in_browser_click',

  /**
   * AUTH_WEBVIEW_COPY_LINK_CLICK Event (Custom)
   *
   * @trigger Fires when the user taps Copy link on the webview dialog.
   *          The copied URL is the original page, not /login or /signup.
   * @params webview, copy_ok, from_page, handoff_target
   * @ga4Category custom
   */
  AUTH_WEBVIEW_COPY_LINK_CLICK: 'auth_webview_copy_link_click',

  /**
   * IDP_CONFIRM_VIEW Event (Custom)
   *
   * @trigger Fires when the Google/Facebook confirm-signup page mounts
   *          (`/confirm`, st-authinfo cookie present).
   * @params idp, is_free_content_flow, from_page, language
   * @ga4Category custom
   */
  IDP_CONFIRM_VIEW: 'idp_confirm_view',

  /**
   * IDP_CONFIRM_SUBMIT Event (Custom)
   *
   * @trigger Fires when the user submits ConfirmSignupForm (createUserWithIdp).
   * @params idp
   * @ga4Category custom
   */
  IDP_CONFIRM_SUBMIT: 'idp_confirm_submit',

  /**
   * IDP_CONFIRM_ABANDON Event (Custom)
   *
   * @trigger Best-effort: fires on pagehide if confirm was viewed but not submitted.
   * @params idp
   * @ga4Category custom
   */
  IDP_CONFIRM_ABANDON: 'idp_confirm_abandon',

  /**
   * SIGNUP_SUCCESS Event (Custom)
   *
   * @trigger Fires after a successful email/password or IdP confirm signup,
   *          alongside the recommended GA4 `sign_up` event.
   * @params method
   * @ga4Category custom
   */
  SIGNUP_SUCCESS: 'signup_success',
};

/**
 * Send Tracking Event to Google Analytics
 *
 * Primary function to send events to Google Analytics 4 via the gtag.js library.
 * This function handles all GA4 event tracking throughout the application.
 *
 * @param {Object} config - Event configuration object
 * @param {string} config.name - Event name (use TRACKING_EVENTS constants)
 * @param {Object} [config.parameters] - Optional event parameters/properties
 *
 * @returns {void}
 *
 * @description
 * This function wraps the Google Analytics gtag() function with safety checks:
 * - Verifies browser environment (doesn't run on server-side)
 * - Checks that gtag is loaded before sending
 * - Validates event name is provided
 * - Catches and logs any errors during sending
 *
 * @example
 * // Simple event with no parameters
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.LOGOUT
 * });
 *
 * @example
 * // Event with parameters
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.SEARCH,
 *   parameters: {
 *     search_term: 'yoga classes',
 *     listing_category_level1: 'yoga',
 *     results_count: 12
 *   }
 * });
 *
 * @example
 * // Event with listing details using helper function
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.SELECT_ITEM,
 *   parameters: getItemEventTrackingParams(listing, config, intl)
 * });
 *
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/events
 */
export const sendTrackingEvent = ({ name, parameters }) => {
  // Check if we're in a browser environment first
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  if (!name) {
    console.error('Event name is required');
    return;
  }

  try {
    window.gtag('event', name, parameters);
  } catch (error) {
    console.error('Error sending tracking event', error);
  }
};

/**
 * Get Item Event Tracking Parameters
 *
 * Helper function that extracts standardized listing parameters for GA4 events.
 * This ensures consistent parameter naming and data structure across all listing-related events.
 *
 * @param {Object} listing - Listing object from Sharetribe API
 * @param {Object} config - Application configuration object
 * @param {Object} intl - Internationalization object from react-intl
 *
 * @returns {Object} Standardized event parameters object
 *
 * @description
 * Extracts and formats listing data into a consistent parameter structure for GA4 events.
 * This function is commonly used with events like:
 * - SELECT_ITEM (when user clicks listing card)
 * - VIEW_ITEM (when viewing checkout page)
 * - BEGIN_CHECKOUT (when starting checkout)
 * - PURCHASE (when completing booking)
 * - BOOKING_SUBMIT_BUTTON_CLICKED (when initiating booking)
 *
 * @example
 * // Usage with SELECT_ITEM event
 * const trackingParams = getItemEventTrackingParams(listing, config, intl);
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.SELECT_ITEM,
 *   parameters: trackingParams
 * });
 *
 * @example
 * // Usage with additional custom parameters
 * sendTrackingEvent({
 *   name: TRACKING_EVENTS.PURCHASE,
 *   parameters: {
 *     ...getItemEventTrackingParams(listing, config, intl),
 *     transaction_id: 'txn_12345',
 *     payment_type: 'credit_card'
 *   }
 * });
 *
 * @returns {Object} Event parameters with the following properties:
 * @returns {string} item_id - Unique listing identifier (UUID)
 * @returns {string} item_name - Listing title
 * @returns {string} item_category - Primary category (e.g., "yoga", "meditation")
 * @returns {string} listing_category_level1 - Same as item_category (for consistency)
 * @returns {string} listing_type - Type of listing (e.g., "group_session", "individual_session")
 * @returns {string} listing_mode - Session delivery mode (e.g., "online", "in-person", "hybrid")
 * @returns {string} listing_venue_name - Name of venue/location (if applicable)
 * @returns {string} price - Formatted price string with currency symbol
 * @returns {string} author_id - Provider's unique user ID
 * @returns {string} author_name - Provider's display name
 *
 * @note
 * The function safely handles missing data using ensureListing() and ensureUser() utilities
 * to prevent errors if listing or author data is incomplete.
 *
 * @see sendTrackingEvent
 */
export const getItemEventTrackingParams = (listing, config, intl) => {
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData, price } = currentListing.attributes;
  const author = ensureUser(currentListing.author);
  const authorName = author.attributes.profile.displayName;
  const { formattedPrice } = getPriceFromListing(currentListing, config, intl);

  const currency = price?.currency || config?.currency || 'USD';
  const numericPrice = price ? convertMoneyToNumber(price) : null;
  const quantity = 1;
  const itemValue = numericPrice !== null ? numericPrice * quantity : null;

  return {
    item_id: id,
    item_name: title,
    item_category: publicData?.categoryLevel1,
    listing_category_level1: publicData?.categoryLevel1,
    listing_type: publicData?.listingType,
    listing_mode: publicData?.mode,
    listing_venue_name: publicData?.venueName,
    price: numericPrice,
    currency,
    quantity,
    value: itemValue,
    formatted_price: formattedPrice,
    author_id: author?.id?.uuid,
    author_name: authorName,
    ...getSubscriptionTrackingFields(currentListing),
  };
};

/**
 * Get Save Action Tracking Parameters
 *
 * Builds event parameters for SAVED_LISTING and UNSAVED_LISTING events.
 *
 * @param {Object} listing - Listing object
 * @param {Object|null} timeslot - Timeslot object (for group sessions) or null
 * @param {Function} getState - Redux getState
 * @param {Object} options - Options object
 * @param {string} options.action - 'save' or 'unsave'
 * @param {string} options.source - 'homepage', 'details', 'saved_events', or 'search'
 * @param {number} savedCountAfter - Total saved items after this action
 * @returns {Object} Event parameters
 */
export const getSaveActionTrackingParams = (listing, timeslot, getState, options) => {
  const state = getState?.() || {};
  const { action, source, savedCountAfter } = options;

  const currentUser = state.user?.currentUser;
  const config = state.marketplaceData?.config;
  const routing = state.routing || {};
  const pagePath =
    routing.currentCanonicalPath ||
    (typeof window !== 'undefined' ? window.location?.pathname || '' : '');
  const previousPage = routing.previousCanonicalPath || '';

  const userId = currentUser?.id?.uuid || 'guest';
  const userType = showCreateListingLinkForUser(config, currentUser) ? 'practitioner' : 'seeker';

  const listingId =
    typeof listing === 'string' ? listing : listing?.uuid ?? listing?.id?.uuid ?? '';
  const listingType = listing?.attributes?.publicData?.listingType;
  const listingTypeLabel = isGroupSession(listingType) ? 'group' : 'individual';

  const timeslotId = !timeslot
    ? null
    : typeof timeslot === 'string'
    ? timeslot
    : timeslot?.uuid ?? timeslot?.id?.uuid ?? null;
  const timeslotStart =
    timeslot?.attributes?.start != null
      ? typeof timeslot.attributes.start === 'string'
        ? timeslot.attributes.start
        : timeslot.attributes.start?.toISOString?.()
      : null;
  const timeslotEnd =
    timeslot?.attributes?.end != null
      ? typeof timeslot.attributes.end === 'string'
        ? timeslot.attributes.end
        : timeslot.attributes.end?.toISOString?.()
      : null;

  return {
    user_id: userId,
    timestamp: new Date().toISOString(),
    action,
    source,
    page_path: pagePath,
    previous_page: previousPage,
    user_type: userType,
    listing_type: listingTypeLabel,
    listing_id: listingId,
    timeslot_id: timeslotId,
    timeslot_start: timeslotStart,
    timeslot_end: timeslotEnd,
    saved_count_after: savedCountAfter,
  };
};

/**
 * Get Save Failure Tracking Parameters
 *
 * Builds event parameters for SAVE_ACTION_FAILED events.
 *
 * @param {Object} listing - Listing object
 * @param {Object|null} timeslot - Timeslot object or null
 * @param {Function} getState - Redux getState
 * @param {Object} options - Options with action and source
 * @param {Error} error - The error that occurred
 * @returns {Object} Event parameters
 */
export const getSaveFailureTrackingParams = (listing, timeslot, getState, options, error) => {
  const state = getState?.() || {};
  const { action, source } = options;

  const currentUser = state.user?.currentUser;
  const routing = state.routing || {};
  const pagePath =
    routing.currentCanonicalPath ||
    (typeof window !== 'undefined' ? window.location?.pathname || '' : '');
  const previousPage = routing.previousCanonicalPath || '';

  const userId = currentUser?.id?.uuid || 'guest';
  const listingId =
    typeof listing === 'string' ? listing : listing?.uuid ?? listing?.id?.uuid ?? '';
  const timeslotId = !timeslot
    ? null
    : typeof timeslot === 'string'
    ? timeslot
    : timeslot?.uuid ?? timeslot?.id?.uuid ?? null;

  let errorMessage = '';
  if (error) {
    const msg = error?.message || String(error);
    errorMessage = msg.length > 100 ? msg.substring(0, 100) : msg;
  }

  return {
    user_id: userId,
    timestamp: new Date().toISOString(),
    action,
    source,
    page_path: pagePath,
    previous_page: previousPage,
    listing_id: listingId,
    timeslot_id: timeslotId,
    error_message: errorMessage,
  };
};

// ========================================
// USER PROPERTIES & INITIAL PAGE VIEW
// ========================================

// Flag to ensure initial page view is only sent once per app load
let initialPageViewSent = false;

/**
 * Set GA4 User Properties
 *
 * Sets user-scoped properties in Google Analytics that persist across all events in the session.
 * This should be called when user logs in, app loads with existing session, or user logs out.
 *
 * @param {Object} options - User properties configuration
 * @param {boolean} options.isLoggedIn - Whether the user is currently logged in
 * @param {string|null} [options.userType=null] - User type (e.g., 'practitioner', 'consumer', 'user')
 * @param {string|null} [options.userId=null] - User's UUID for cross-device tracking
 *
 * @example
 * // Set properties for logged-in user
 * setUserProperties({
 *   isLoggedIn: true,
 *   userType: 'practitioner',
 *   userId: 'abc-123-def'
 * });
 *
 * @example
 * // Clear properties on logout
 * setUserProperties({ isLoggedIn: false });
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-properties
 */
export const setUserProperties = ({ isLoggedIn, userType = null, userId = null }) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    // Set user_id for cross-device tracking (GA4 recommended)
    // Setting to null clears the user_id
    window.gtag('set', 'user_id', userId);

    // Set custom user properties
    window.gtag('set', 'user_properties', {
      login_status: isLoggedIn ? 'logged_in' : 'guest',
      user_type: userType || 'guest',
    });
  } catch (error) {
    console.error('Error setting user properties', error);
  }
};

/**
 * Send Initial Page View
 *
 * Sends the first page_view event after user properties have been set.
 * This is only called once on app initialization, after user data is loaded.
 * Subsequent page views are handled by the analytics middleware in src/analytics/handlers.js.
 *
 * @description
 * Since we disable automatic page_view in gtag config (send_page_view: false),
 * we need to manually send the initial page view after user properties are set.
 * This ensures the first page view includes user context (logged_in status, user_type).
 *
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
 */
export const sendInitialPageView = () => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  // Only send initial page view once per app load
  if (initialPageViewSent) {
    return;
  }

  try {
    window.gtag('event', 'page_view', {
      page_path: window.location.pathname,
      page_title: document.title,
    });
    initialPageViewSent = true;
  } catch (error) {
    console.error('Error sending initial page view', error);
  }
};
